import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import AdBanner from './components/AdBanner';
import CartSidebar from './components/CartSidebar';
import Footer from './components/Footer';
import Header from './components/Header';
import { CartProvider, useCart } from './contexts/CartContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';

// Public pages
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Categories = lazy(() => import('./pages/Categories'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const CommunicationPolicy = lazy(() => import('./pages/CommunicationPolicy'));
const CancellationPolicy = lazy(() => import('./pages/CancellationPolicy'));
const DrugPolicy = lazy(() => import('./pages/DrugPolicy'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const ProductRedirect = lazy(() => import('./components/ProductRedirect'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Public layout — Header, Footer, AdBanners
const PublicLayout = () => {
  const { isCartOpen, closeCart } = useCart();
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <AdBanner type="horizontal" className="border-b border-gray-100 bg-white py-1" />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:productSlug" element={<ProductDetail />} />
            {/* 301 Redirect from old URL format to new SEO-friendly format */}
            <Route path="/product/:productId" element={<ProductRedirect />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/category/:categorySlug" element={<CategoryPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/shipping" element={<ShippingPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/communication-policy" element={<CommunicationPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/drug-policy" element={<DrugPolicy />} />
            <Route path="/track-order" element={<TrackOrder />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/meadmin/products/legacy" element={<Navigate to="/meadmin/products" replace />} />
          </Routes>
        </Suspense>
      </main>
      <AdBanner type="horizontal" className="border-t border-gray-100 bg-white py-1" />
      <Footer />
      <CartSidebar isOpen={isCartOpen} onClose={closeCart} />
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <AdminAuthProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Admin — full-screen, no Header/Footer */}
              <Route path="/meadmin/login" element={<AdminLogin />} />
              <Route path="/meadmin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Public — with Header/Footer */}
              <Route path="/*" element={<PublicLayout />} />
            </Routes>
          </Suspense>
        </Router>
      </AdminAuthProvider>
    </CartProvider>
  );
}

export default App;
